import React, { useState, useEffect, useCallback, useRef, forwardRef } from 'react';
import { B } from '../../constants';
import { borderRadius, transitions } from '../../designTokens';

const sizeMap = {
  xs: { p: '6px 10px', fs: 12, is: 14, bl: 10, br: 10 },
  sm: { p: '8px 12px', fs: 13, is: 15, bl: 12, br: 12 },
  md: { p: '12px 16px', fs: 14, is: 16, bl: 16, br: 16 },
  lg: { p: '14px 20px', fs: 16, is: 18, bl: 20, br: 20 },
  xl: { p: '16px 24px', fs: 18, is: 20, bl: 24, br: 24 },
};

const radiusMap = {
  xs: borderRadius.xs,
  sm: borderRadius.sm,
  md: borderRadius.md,
  lg: borderRadius.lg,
  xl: borderRadius.xl,
  pill: borderRadius.pill,
};

function LoadingSpinner({ color = B.muted, size = 14 }) {
  return (
    <span
      style={{
        display: 'inline-flex', width: size, height: size,
        border: `2px solid ${color}33`, borderTopColor: color,
        borderRadius: '50%', animation: 'spin 0.6s linear infinite',
        flexShrink: 0,
      }}
    />
  );
}

function PasswordToggle({ visible, onToggle }) {
  return (
    <span
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onToggle(); }}
      aria-label={visible ? 'Hide password' : 'Show password'}
      style={{
        position: 'absolute', right: 14, zIndex: 1,
        cursor: 'pointer', fontSize: 16, lineHeight: 1,
        color: B.muted, userSelect: 'none',
      }}
    >
      {visible ? '🙈' : '👁️'}
    </span>
  );
}

function ClearButton({ onClear }) {
  return (
    <span
      onClick={onClear}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClear(); }}
      aria-label="Clear input"
      style={{
        position: 'absolute', right: 14, zIndex: 1,
        cursor: 'pointer', fontSize: 18, lineHeight: 1,
        color: B.muted, userSelect: 'none',
      }}
    >
      ✕
    </span>
  );
}

let idCounter = 0;

const Input = forwardRef(({
  as: Tag = 'input',
  type = 'text',
  placeholder,
  value,
  onChange,
  icon,
  leftIcon,
  rightIcon,
  error,
  success,
  helperText,
  size = 'md',
  radius = 'md',
  disabled,
  loading,
  clearable,
  onClear,
  showPasswordToggle,
  maxLength,
  showCharCount,
  prefix,
  suffix,
  focusRingColor = B.forest,
  showFocusRing = true,
  fullWidth = true,
  transitionDuration = 'normal',
  style,
  multiline,
  rows = 3,
  rowsMax,
  autoResize,
  ...props
}, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const innerRef = useRef(null);
  const helperIdRef = useRef(null);

  if (!helperIdRef.current) {
    helperIdRef.current = `input-helper-${++idCounter}`;
  }

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handler = (e) => setReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const s = sizeMap[size] || sizeMap.md;
  const r = radiusMap[radius] || radiusMap.md;
  const transitionValue = transitions[transitionDuration] || transitionDuration || transitions.normal;

  const isTextarea = multiline || Tag === 'textarea';
  const actualTag = isTextarea ? 'textarea' : Tag;

  const effectiveLeftIcon = leftIcon || icon;
  const hasLeftIcon = !!effectiveLeftIcon;
  const hasPrefix = !!prefix;
  const hasSuffix = !!suffix;
  const hasValue = value !== undefined && value !== null && value !== '';
  const isPassword = type === 'password' && !passwordVisible;
  const isDisabled = disabled || loading;

  const showLoading = !!loading;
  const showClear = clearable && hasValue && !isDisabled && !showLoading;
  const showPassword = showPasswordToggle && type === 'password' && !showLoading && !showClear;
  const showRightIcon = !!rightIcon && !showLoading && !showClear && !showPassword;
  const hasRightElement = showLoading || showClear || showPassword || showRightIcon;

  const paddingLeft = s.bl + (hasLeftIcon ? 28 : 0) + (hasPrefix ? 36 : 0);
  const paddingRight = s.br + (hasRightElement ? 28 : 0) + (hasSuffix ? 36 : 0);

  const borderColor = error ? '#ef4444' : success ? B.forest : isFocused ? B.forest : '#e5e0d8';
  const boxShadow = isFocused && !error && !success && showFocusRing ? `0 0 0 3px ${focusRingColor}25` : 'none';

  const setRef = useCallback((node) => {
    innerRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  }, [ref]);

  useEffect(() => {
    if (autoResize && isTextarea && innerRef.current) {
      innerRef.current.style.height = 'auto';
      const newH = innerRef.current.scrollHeight;
      if (rowsMax) {
        const lineH = s.fs * 1.5;
        const maxH = rowsMax * lineH;
        innerRef.current.style.height = Math.min(newH, maxH) + 'px';
      } else {
        innerRef.current.style.height = newH + 'px';
      }
    }
  }, [autoResize, isTextarea, value, rowsMax, s.fs]);

  const handleChange = useCallback((e) => {
    if (maxLength && e.target.value.length > maxLength) {
      e.target.value = e.target.value.slice(0, maxLength);
    }
    if (autoResize && isTextarea) {
      e.currentTarget.style.height = 'auto';
      const newH = e.currentTarget.scrollHeight;
      if (rowsMax) {
        const lineH = s.fs * 1.5;
        const maxH = rowsMax * lineH;
        e.currentTarget.style.height = Math.min(newH, maxH) + 'px';
      } else {
        e.currentTarget.style.height = newH + 'px';
      }
    }
    onChange?.(e);
  }, [onChange, maxLength, autoResize, isTextarea, rowsMax, s.fs]);

  const handleFocus = useCallback(() => setIsFocused(true), []);
  const handleBlur = useCallback(() => setIsFocused(false), []);

  const handleClear = useCallback((e) => {
    e.stopPropagation();
    onChange?.({ target: { value: '' } });
    onClear?.(value);
  }, [onChange, onClear, value]);

  const handlePasswordToggle = useCallback(() => {
    setPasswordVisible((prev) => !prev);
  }, []);

  const charCount = hasValue && maxLength ? `${String(value).length} / ${maxLength}` : null;
  const helperId = helperIdRef.current;

  const renderRightElement = () => {
    if (showLoading) return <LoadingSpinner color={B.muted} size={s.is - 2} />;
    if (showClear) return <ClearButton onClear={handleClear} />;
    if (showPassword) return <PasswordToggle visible={passwordVisible} onToggle={handlePasswordToggle} />;
    if (showRightIcon) {
      return (
        <span
          style={{
            position: 'absolute', right: 14, zIndex: 1,
            pointerEvents: 'none', fontSize: s.is,
            color: error ? '#ef4444' : success ? B.forest : B.muted,
          }}
        >
          {rightIcon}
        </span>
      );
    }
    return null;
  };

  const commonStyle = {
    width: '100%',
    padding: s.p,
    paddingLeft,
    paddingRight,
    fontSize: s.fs,
    fontFamily: "'Montserrat',sans-serif",
    color: B.night,
    background: isDisabled ? '#f5f5f5' : 'white',
    border: `2px solid ${borderColor}`,
    borderRadius: r,
    outline: 'none',
    boxShadow,
    transition: reducedMotion ? 'none' : `all ${transitionValue}`,
    boxSizing: 'border-box',
    cursor: isDisabled ? 'not-allowed' : 'text',
    opacity: isDisabled ? 0.6 : 1,
    ...(isTextarea ? { resize: 'vertical', minHeight: rows * s.fs * 1.5 + 24 } : {}),
    ...style,
    ...(error && { border: `2px solid #ef4444`, background: '#fef2f2' }),
    ...(success && !error && { border: `2px solid ${B.forest}`, background: '#f0fdf4' }),
  };

  const elementProps = {
    ref: setRef,
    placeholder,
    value,
    disabled: isDisabled,
    onChange: handleChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    style: commonStyle,
    'aria-invalid': !!error,
    'aria-describedby': helperText ? helperId : undefined,
    ...(maxLength ? { maxLength } : {}),
    ...props,
  };

  if (isTextarea) {
    elementProps.rows = rows;
  } else if (actualTag === 'input') {
    elementProps.type = isPassword ? 'password' : type === 'password' ? 'text' : type;
  }

  return (
    <div style={{ width: fullWidth ? '100%' : 'auto', display: fullWidth ? 'block' : 'inline-block' }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        {hasLeftIcon && (
          <span
            style={{
              position: 'absolute', left: 14, zIndex: 1,
              pointerEvents: 'none', fontSize: s.is,
              color: error ? '#ef4444' : success ? B.forest : B.muted,
            }}
          >
            {effectiveLeftIcon}
          </span>
        )}
        {hasPrefix && (
          <span
            style={{
              position: 'absolute', left: hasLeftIcon ? 42 : 14, zIndex: 1,
              pointerEvents: 'none', fontSize: s.fs,
              color: B.muted, whiteSpace: 'nowrap',
            }}
          >
            {prefix}
          </span>
        )}
        {React.createElement(actualTag, elementProps)}
        {hasSuffix && !hasRightElement && (
          <span
            style={{
              position: 'absolute', right: 14, zIndex: 1,
              pointerEvents: 'none', fontSize: s.fs,
              color: B.muted, whiteSpace: 'nowrap',
            }}
          >
            {suffix}
          </span>
        )}
        {renderRightElement()}
      </div>
      {(helperText || (showCharCount && charCount)) && (
        <div
          id={helperId}
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginTop: 6, fontSize: 12,
            fontFamily: "'Montserrat',sans-serif",
          }}
        >
          {helperText && (
            <span style={{ color: error ? '#ef4444' : success ? B.forest : B.muted }}>
              {helperText}
            </span>
          )}
          {showCharCount && charCount && (
            <span style={{
              color: String(value).length >= maxLength ? '#ef4444'
                : String(value).length >= maxLength * 0.8 ? '#f59e0b'
                : B.muted,
            }}>
              {charCount}
            </span>
          )}
        </div>
      )}
    </div>
  );
});

export default Input;
